// Conversion optimization utilities for launch marketing

// Track scroll depth for engagement analysis
export function initScrollDepthTracking() {
  if (typeof window === 'undefined') return;

  let maxScrollDepth = 0;
  const milestones = [25, 50, 75, 90, 100];
  const tracked = new Set();

  const trackScrollDepth = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement?.scrollHeight;
    const scrollTop = window.scrollY || document.documentElement?.scrollTop;
    const scrollPercent = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);

    if (scrollPercent > maxScrollDepth) {
      maxScrollDepth = scrollPercent;

      milestones?.forEach(milestone => {
        if (scrollPercent >= milestone && !tracked?.has(milestone)) {
          tracked?.add(milestone);
          if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'scroll_depth', {
              percent: milestone,
              page: window.location?.pathname
            });
          }
        }
      });
    }
  };

  window.addEventListener('scroll', trackScrollDepth, { passive: true });
  return () => window.removeEventListener('scroll', trackScrollDepth);
}

// Track time on page for engagement metrics
export function trackTimeOnPage() {
  if (typeof window === 'undefined') return;

  const startTime = Date.now();
  const milestones = [10, 30, 60, 120, 300]; // seconds
  const tracked = new Set();

  const checkTimeOnPage = () => {
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);

    milestones?.forEach(milestone => {
      if (timeSpent >= milestone && !tracked?.has(milestone)) {
        tracked?.add(milestone);
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'time_on_page', {
            seconds: milestone,
            page: window.location?.pathname
          });
        }
      }
    });
  };

  const interval = setInterval(checkTimeOnPage, 5000);
  return () => clearInterval(interval);
}

// Track exit intent (when user is about to leave)
export function initExitIntentTracking(callback) {
  if (typeof window === 'undefined') return;

  let hasTriggered = false;

  const handleMouseLeave = (e) => {
    if (hasTriggered) return;
    if (e?.clientY <= 0) {
      hasTriggered = true;
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'exit_intent', {
          page: window.location?.pathname
        });
      }
      if (callback) callback();
    }
  };

  document.addEventListener('mouseleave', handleMouseLeave);
  return () => document.removeEventListener('mouseleave', handleMouseLeave);
}

// Track CTA visibility (when CTA enters viewport)
export function trackCTAVisibility(elementId) {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

  const element = document.getElementById(elementId);
  if (!element) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'cta_view', {
              cta_id: elementId,
              page: window.location.pathname
            });
          }
          observer.disconnect();
        }
      });
    },
    { threshold: 0.5 }
  );

  observer?.observe(element);
  return () => observer?.disconnect();
}

// A/B test variant tracker
export function trackABTestVariant(testName, variant) {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'ab_test_view', {
      test_name: testName,
      variant: variant,
      page: window.location?.pathname
    });
  }
}

// Track form field interactions
export function trackFormInteraction(formId, fieldName) {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'form_interaction', {
      form_id: formId,
      field_name: fieldName,
      page: window.location?.pathname
    });
  }
}

// Track video play events
export function trackVideoPlay(videoId, videoTitle) {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'video_play', {
      video_id: videoId,
      video_title: videoTitle,
      page: window.location?.pathname
    });
  }
}

// Track pricing plan views
export function trackPricingView(planName) {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'pricing_view', {
      plan_name: planName,
      page: window.location?.pathname
    });
  }
}